import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { useMeContext } from '../api/me-provider.js';
import { newOneTimePassword, readFirstSignIn, requestSetupEmail, turnOffSignIn } from '../api/athlete-sign-in.js';
import { ApiError } from '../api/client.js';
import type { Person, SignIn } from '../api/use-people.js';
import { OneTimePassword } from './one-time-password.js';
import { BusyLabel } from './busy-label.js';

/**
 * "Noa's own sign-in", on a child's page (*Athlete Portal* canvas, rows 1 and
 * 5 and 13; ruled 14-17 October). Four states, each as drawn:
 *
 *   none       what Noa would get, and Let Noa sign in
 *   link sent  Check your email, and Send it again
 *   on         the username, when it was activated, the first password, and
 *              Deactivate
 *   off        the username, when it was deactivated, and Reactivate
 *
 * THE FIRST PASSWORD (row 13, 17 October). Straight after activating, the
 * one-time password arrives in the navigation from the setup page and is shown
 * once: it is taken into this component and the navigation entry cleared, so
 * a reload or Back does not show it again. The page opens this row for it,
 * and it is scrolled into view (canvas row F, 26 October). Otherwise "First sign-in" is read
 * from Microsoft: Not yet, with New one-time password, or the day Noa chose
 * their own.
 *
 * The state is held here and moved on by each answer, as ChildConsent does,
 * rather than re-reading the account, which would blank the page to say what
 * it already says.
 *
 * Deactivating takes 10-17 seconds (the directory waits for Microsoft to read
 * it back), so the button says it is working for that long.
 */

type View = { kind: 'none' } | { kind: 'sent'; to: string | null } | { kind: 'on' } | { kind: 'off' };

function viewOf(signIn: SignIn | null): View {
  if (signIn?.enabled) return { kind: 'on' };
  if (signIn?.link_until) return { kind: 'sent', to: null };
  if (signIn?.username) return { kind: 'off' };
  return { kind: 'none' };
}

export function ChildSignIn({ person }: { person: Person }) {
  const getToken = useAccessToken();
  const { state: meState } = useMeContext();
  const [signIn, setSignIn] = useState<SignIn | null>(person.sign_in);
  const [view, setView] = useState<View>(() => viewOf(person.sign_in));
  const [working, setWorking] = useState<'ask' | 'one-time' | 'off' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [oneTime, setOneTime] = useState<string | null>(
    () => (location.state as { oneTimePassword?: string } | null)?.oneTimePassword ?? null,
  );
  const [first, setFirst] = useState<{ signed_in: boolean; at: string | null } | null>(null);
  const shown = useRef<HTMLDivElement>(null);
  const [arrived] = useState(() => oneTime !== null);

  useEffect(() => {
    if (arrived) shown.current?.scrollIntoView({ block: 'center' });
  }, [arrived]);

  useEffect(() => {
    if ((location.state as { oneTimePassword?: string } | null)?.oneTimePassword) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const on = view.kind === 'on';
  useEffect(() => {
    if (!on || oneTime) return;
    let cancelled = false;
    readFirstSignIn(getToken, person.client_id)
      .then((state) => {
        if (!cancelled) setFirst(state);
      })
      .catch(() => {
        // Left unsaid rather than guessed: the page reads as before without it.
      });
    return () => {
      cancelled = true;
    };
  }, [on, oneTime, getToken, person.client_id]);

  const name = person.first_name;
  const myEmail = meState.status === 'ready' ? meState.me.email : null;

  async function ask() {
    if (working) return;
    setWorking('ask');
    setProblem(null);
    try {
      const sent = await requestSetupEmail(getToken, person.client_id);
      setView({ kind: 'sent', to: sent.sent_to });
    } catch {
      setProblem('That did not send. Try again.');
    } finally {
      setWorking(null);
    }
  }

  async function anotherOneTime() {
    if (working) return;
    setWorking('one-time');
    setProblem(null);
    try {
      const made = await newOneTimePassword(getToken, person.client_id);
      setOneTime(made.one_time_password);
    } catch (cause) {
      // Noa chose their own since the page loaded: say so rather than fail.
      if (cause instanceof ApiError && cause.code === 'already_signed_in') {
        setFirst(await readFirstSignIn(getToken, person.client_id).catch(() => null));
      } else setProblem('That did not work. Try again.');
    } finally {
      setWorking(null);
    }
  }

  async function turnOff() {
    if (working) return;
    setWorking('off');
    setProblem(null);
    try {
      const off = await turnOffSignIn(getToken, person.client_id);
      setSignIn((current) => current && { ...current, enabled: false, turned_off_at: off.turned_off_at });
      setView({ kind: 'off' });
    } catch {
      setProblem(`${name}'s access is still active. Try again.`);
    } finally {
      setWorking(null);
    }
  }

  return (
    <>
      <span className="section-label">{name}'s own sign-in</span>

      {view.kind === 'none' ? (
        <section className="panel">
          <p className="statement">
            {name} can sign in to see their lessons, read the coach's notes and practice plans, and send two videos
            between lessons.
          </p>
          <ol className="welcome-steps">
            <li>We email you a link to choose {name}'s username.</li>
            <li>We show you a one-time password. Give it to {name}.</li>
            <li>{name} signs in and chooses their own password.</li>
          </ol>
          <button type="button" className="primary" onClick={() => void ask()} disabled={working !== null} aria-busy={working === 'ask'}>
            <BusyLabel busy={working === 'ask'} working="Sending…">
              {`Let ${name} sign in`}
            </BusyLabel>
          </button>
        </section>
      ) : null}

      {view.kind === 'sent' ? (
        <>
          <div className="done" role="status">
            <span className="head">Check your email</span>
            <p className="statement">
              We sent a link to {view.to ?? myEmail ?? 'your email'}.{' '}
              {signIn?.username
                ? `Open it to reactivate ${name}'s access.`
                : `Open it to choose ${name}'s username.`}{' '}
              The link works once, for 24 hours.
            </p>
          </div>
          <button type="button" className="secondary" onClick={() => void ask()} disabled={working !== null} aria-busy={working === 'ask'}>
            <BusyLabel busy={working === 'ask'} working="Sending…">
              Send it again
            </BusyLabel>
          </button>
        </>
      ) : null}

      {view.kind === 'on' ? (
        <>
          <Fact label="Username" value={signIn?.username ?? ''} />
          {signIn?.turned_on_at ? <Fact label="Activated" value={day(signIn.turned_on_at)} /> : null}
          {oneTime ? (
            <>
              <span className="section-label">One-time password</span>
              <OneTimePassword value={oneTime} boxRef={shown} />
              <p className="statement">Give {name} this. It is shown once.</p>
            </>
          ) : first && !first.signed_in ? (
            <>
              <Fact label="First sign-in" value="Not yet" />
              <button type="button" className="secondary" onClick={() => void anotherOneTime()} disabled={working !== null} aria-busy={working === 'one-time'}>
                <BusyLabel busy={working === 'one-time'} working="Making…">
                  New one-time password
                </BusyLabel>
              </button>
            </>
          ) : first?.at ? (
            <>
              <Fact label="First sign-in" value={day(first.at)} />
              <p className="statement">
                If {name} forgets their password, they tap <b>Forgot password?</b> and the code comes to your email.
              </p>
            </>
          ) : null}
          <button type="button" className="warn-outline" onClick={() => void turnOff()} disabled={working !== null} aria-busy={working === 'off'}>
            <BusyLabel busy={working === 'off'} working="Deactivating… about 15 seconds">
              {`Deactivate ${name}'s access`}
            </BusyLabel>
          </button>
        </>
      ) : null}

      {view.kind === 'off' ? (
        <>
          <Fact label="Username" value={signIn?.username ?? ''} />
          {signIn?.turned_off_at ? <Fact label="Deactivated" value={day(signIn.turned_off_at)} /> : null}
          <button type="button" className="primary" onClick={() => void ask()} disabled={working !== null} aria-busy={working === 'ask'}>
            <BusyLabel busy={working === 'ask'} working="Sending…">
              {`Reactivate ${name}'s access`}
            </BusyLabel>
          </button>
        </>
      ) : null}

      {problem ? (
        <p className="form-note" role="alert">
          {problem}
        </p>
      ) : null}
    </>
  );
}

/** "Thu 15 Oct", as drawn. */
function day(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="fact">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </div>
  );
}
